import { Numpad, type NumpadKey } from '@mobile/components/kartoy/numpad'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import { selectedCardTopupLoading, topupCard } from '@shared-core/features/backend'
import { useCardRates } from '@shared-core/hooks/use-card-rates'
import { formatInputAmount as formatAmount, useCurrencyAmount } from '@shared-core/hooks/use-currency-amount'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { type RootRouteProps, ScreensList, useNavigation, useRoute } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { Big } from '@shared-core/utils/balances/big'
import { AmountChip } from '@shared-ui/components/ui/amount-chip'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Row } from '@shared-ui/components/ui/grid'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { useEffect } from 'react'
import { Pressable, View } from 'react-native'
import Animated, {
  Easing,
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

const CHIPS = ['100', '500', '1000'] as const
export function ScreenTopupAmount() {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const route = useRoute<RootRouteProps<ScreensList.TOPUP_AMOUNT>>()
  const haptic = useHaptic()
  const dispatch = useAppDispatch()
  const cardTopupLoading = useAppSelector(selectedCardTopupLoading)
  const { cardId, topupRate: rate } = useCardRates(route.params?.cardId)
  const currencyAmount = useCurrencyAmount(rate)

  const pop = useSharedValue(0)
  const conversionAnimation = useSharedValue(0)

  // Пружина-«pop» всей суммы при вводе цифры
  useEffect(() => {
    pop.value = withSequence(withTiming(1, { duration: 90 }), withTiming(0, { duration: 220 }))
  }, [pop, currencyAmount.raw])

  const amountStyle = useAnimatedStyle(() => ({
    opacity: 1 - conversionAnimation.value * 0.28,
    transform: [{ scale: (1 + pop.value * 0.045) * (1 - conversionAnimation.value * 0.035) }],
  }))
  const swapAnimationStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${conversionAnimation.value * 180}deg` }],
  }))

  const isActive = Boolean(cardId && rate) && Big(currencyAmount.amount).gt(0)
  const mainAmount =
    currencyAmount.inputCurrency === 'usd'
      ? `$${formatAmount(currencyAmount.raw) || 0}`
      : `₽${formatAmount(currencyAmount.raw) || 0}`
  const convertedAmount =
    currencyAmount.inputCurrency === 'usd'
      ? `₽${formatAmount(currencyAmount.amountRub.toFixed(2).replace('.', ','))}`
      : `$${formatAmount(currencyAmount.amountUsd.toFixed(2).replace('.', ','))}`

  // Ввод цифры/запятой с ограничениями длины и дробной части.
  const handleKey = (key: NumpadKey) => {
    haptic('impactLight')
    currencyAmount.events.handleKey(key)
  }

  const handleSubmit = async () => {
    if (!isActive || cardTopupLoading) return

    if (!cardId) return
    const result = await dispatch(topupCard({ cardId, amount: currencyAmount.amountRub.toFixed(2) }))

    if (topupCard.fulfilled.match(result) && result.payload)
      navigation.navigate(ScreensList.PAYMENT_LINK, { topup: result.payload })
  }

  const handleConversion = () => {
    if (!rate) return
    haptic('selection')
    conversionAnimation.value = withSequence(
      withTiming(1, { duration: 140, easing: Easing.inOut(Easing.cubic) }),
      withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) }),
    )
    currencyAmount.events.convert()
  }

  return (
    <ScreenWrapperWithoutScroll style={styles.container}>
      <Header
        isBackButton
        exitCross={false}
        title={
          <Animated.View entering={FadeInDown.delay(40).duration(380)} style={styles.headerBlock}>
            <GText fw={600} style={styles.headerText}>
              {t('title')}
            </GText>
            <GText fw={500} style={styles.rate}>
              {rate ? `$1 = ₽${formatAmount(rate.replace('.', ','))}` : '—'}
            </GText>
          </Animated.View>
        }
      />

      <Animated.View entering={FadeInDown.delay(100).duration(400)} style={styles.amountBlock}>
        <Animated.View style={amountStyle}>
          <GText fw={600} style={styles.amount}>
            {mainAmount}
          </GText>
        </Animated.View>
        <Pressable accessibilityRole="button" hitSlop={8} onPress={handleConversion}>
          <Row align="center" gap={4}>
            <AppIcon icon="amount-equal" size={20} />
            <GText fw={600} style={styles.converted}>
              {rate ? convertedAmount : '—'}
            </GText>
            <View style={styles.swapButton}>
              <Animated.View style={swapAnimationStyle}>
                <AppIcon icon="amount-swap" size={16} />
              </Animated.View>
            </View>
          </Row>
        </Pressable>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(160).duration(400)} style={styles.chipsRow}>
        <Row gap={12}>
          {CHIPS.map((chip) => (
            <AmountChip
              key={chip}
              label={`$${formatAmount(chip)}`}
              onPress={() => {
                haptic('impactLight')
                currencyAmount.events.setInputCurrency('usd')
                currencyAmount.events.setRaw(chip)
              }}
            />
          ))}
        </Row>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(220).duration(400)} style={styles.numpadWrap}>
        <Numpad onPressKey={handleKey} />
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(280).duration(400)} style={styles.ctaWrap}>
        <PillButton
          title={isActive ? t('action') : t('action-empty')}
          variant={isActive ? 'primary' : 'secondary'}
          disabled={!isActive || cardTopupLoading}
          onPress={handleSubmit}
        />
      </Animated.View>
    </ScreenWrapperWithoutScroll>
  )
}
